import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, CheckCircle, BarChart3, MessageSquare, Zap, Layout } from "lucide-react";
import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "@/lib/firebase";

const ADMIN_URL = import.meta.env.VITE_ADMIN_URL || "http://localhost:4001";

const LandingPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (!auth) return;

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user && user.emailVerified);
    });
    return unsubscribe;
  }, []);

  const features = [
    {
      icon: <MessageSquare className="h-8 w-8 text-primary" />,
      title: "Collect feedback",
      description: "Gather product feedback from customers with customizable forms and surveys."
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-brand-coral" />,
      title: "Spot patterns",
      description: "Use AI-powered sentiment analysis and keyword extraction to understand what is changing."
    },
    {
      icon: <Layout className="h-8 w-8 text-brand-magenta" />,
      title: "Organize priorities",
      description: "Monitor feedback across every product from one focused workspace."
    },
    {
      icon: <Zap className="h-8 w-8 text-accent" />,
      title: "Move to action",
      description: "Turn feedback themes into clearer decisions for what to improve next."
    }
  ];

  const benefits = [
    "Real-time feedback collection",
    "AI-powered sentiment analysis", 
    "Customizable feedback forms",
    "Unlimited product management",
    "Secure and GDPR compliant",
    "Easy integration and setup"
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img src="/logo.png" alt="senloop" className="w-8 h-8 rounded-lg" />
            <span className="font-brand text-3xl font-extrabold tracking-tight text-foreground">senloop</span>
          </div>
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <a href={`${ADMIN_URL}/app`}>
                <Button>Go to Dashboard</Button>
              </a>
            ) : (
              <>
                <a href={`${ADMIN_URL}/login`}>
                  <Button variant="ghost">Sign In</Button>
                </a>
                <a href={`${ADMIN_URL}/register`}>
                  <Button>Get Started</Button>
                </a>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <Badge variant="secondary" className="mb-4 border border-border bg-secondary/80">
          <Zap className="h-3 w-3 mr-1" />
          Now with AI-powered insights
        </Badge>
        <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight text-foreground mb-6">
          Product feedback,
          <span className="text-primary"> organized for action.</span>
        </h1>
        <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
          senloop helps product teams collect, understand, and prioritize customer feedback across every product in one focused workspace.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {isAuthenticated ? (
            <a href={`${ADMIN_URL}/app`}>
              <Button size="lg" className="text-lg px-8 py-6">
                Go to Dashboard
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          ) : (
            <>
              <a href={`${ADMIN_URL}/register`}>
                <Button size="lg" className="text-lg px-8 py-6">
                  Create Free Account
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-4">
            Everything You Need to Collect Better Feedback
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            From simple feedback forms to advanced analytics, we've got you covered.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <Card key={index} className="text-center border-border/70 shadow-lg bg-card/85 backdrop-blur-sm">
              <CardHeader>
                <div className="flex justify-center mb-4">
                  {feature.icon}
                </div>
                <CardTitle className="text-xl">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-card/75 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Why choose senloop?
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Built for modern teams who need powerful, yet simple feedback collection.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                <span className="text-lg text-foreground">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <div className="bg-gradient-to-r from-primary via-brand-coral to-brand-amber rounded-2xl p-12 text-primary-foreground shadow-xl">
          <h2 className="text-4xl font-bold mb-4">
            Ready to organize your product feedback?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Use senloop to turn customer feedback into clearer product decisions.
          </p>
          <a href={`${ADMIN_URL}/register`}>
            <Button size="lg" variant="secondary" className="text-lg px-8 py-6">
              Start Collecting Feedback Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-surface-deep text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src="/logo.png" alt="senloop" className="w-8 h-8 rounded-lg" />
                <span className="font-brand text-3xl font-extrabold tracking-tight">senloop</span>
              </div>
              <p className="text-surface-deep-foreground">
                Product feedback, organized for action.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-surface-deep-foreground">
                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-surface-deep-foreground">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-surface-deep-foreground">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 text-center text-surface-deep-foreground">
            <p>&copy; 2024 senloop. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
